// =============================================================================
// MeuDriver — PushNotificationToggle
//
// Toggle para ativar/desativar push notifications via VAPID.
// Usado em StudentProfile.tsx e InstructorEditProfile.tsx.
// =============================================================================

import { Bell, BellOff, Loader2 } from "lucide-react";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { Button } from "@/components/ui/button";

interface PushNotificationToggleProps {
  /** Texto opcional mostrado ao lado do toggle. Default: "Notificações push". */
  label?: string;
  /** Classe CSS adicional para o wrapper. */
  className?: string;
}

export function PushNotificationToggle({
  label = "Notificações push",
  className,
}: PushNotificationToggleProps) {
  const { isSupported, permission, subscribe, unsubscribe, isLoading } =
    usePushNotifications();

  if (!isSupported) {
    return (
      <div
        className={`flex items-center gap-2 text-sm text-muted-foreground ${className ?? ""}`}
      >
        <BellOff className="h-4 w-4" />
        <span>Notificações não suportadas neste dispositivo</span>
      </div>
    );
  }

  const isEnabled = permission === "granted";
  const isDenied = permission === "denied";

  return (
    <div
      className={`flex items-center justify-between gap-4 rounded-lg border border-gray-200 bg-white px-4 py-3 ${className ?? ""}`}
    >
      <div className="flex items-center gap-3 min-w-0">
        {isEnabled ? (
          <Bell className="h-4 w-4 text-brand-600 shrink-0" />
        ) : (
          <BellOff className="h-4 w-4 text-muted-foreground shrink-0" />
        )}
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-800">{label}</p>
          {isDenied && (
            <p className="text-xs text-amber-600 mt-0.5">
              Permissão negada. Habilite nas configurações do navegador.
            </p>
          )}
          {isEnabled && (
            <p className="text-xs text-green-600 mt-0.5">Ativas</p>
          )}
        </div>
      </div>

      <Button
        type="button"
        variant={isEnabled ? "outline" : "default"}
        size="sm"
        disabled={isLoading || isDenied}
        onClick={isEnabled ? unsubscribe : subscribe}
        data-testid="push-notification-toggle"
        className="shrink-0"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : isEnabled ? (
          "Desativar"
        ) : (
          "Ativar"
        )}
      </Button>
    </div>
  );
}
